import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useCRM } from '../context/CRMContext';
import {
  BarChart, Bar, Tooltip, Legend, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, Cell,
} from 'recharts';
import {
  subscribeUserSyncedMeetings, subscribeSyncedMeetings,
  subscribeEmailActivity, subscribeWorkLogs, subscribeUserWorkLogs,
  addWorkLog, deleteWorkLog, subscribeWorkTargets, saveWorkTarget,
  subscribeAllRemarkThreadsMeta, subscribeRemarkMessages,
  addRemarkMessage, markRemarkThreadSeen,
} from '../firebase/firestore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// ─── Constants ────────────────────────────────────────────────────────────────
const LOG_CATEGORIES = [
  { id: 'meeting',  label: 'Meeting (not auto-synced)', icon: '🤝' },
  { id: 'call',     label: 'Call', icon: '📞' },
  { id: 'followup', label: 'Follow-up / Outreach', icon: '✉️' },
  { id: 'admin',    label: 'Admin / Paperwork', icon: '📋' },
  { id: 'other',    label: 'Other', icon: '🗂️' },
];
const CATEGORY_MAP = Object.fromEntries(LOG_CATEGORIES.map(c => [c.id, c]));

const RANGE_OPTIONS = [
  { id: 7,  label: 'Last 7 days' },
  { id: 30, label: 'Last 30 days' },
  { id: 90, label: 'Last 90 days' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function toISODate(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function todayIso() { return toISODate(new Date()); }
function daysAgoIso(days) { return toISODate(new Date(Date.now() - days * 24 * 60 * 60 * 1000)); }
function daysAgoMs(days) { return Date.now() - days * 24 * 60 * 60 * 1000; }
function shiftDateIso(dateStr, delta) {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + delta);
  return toISODate(d);
}

function formatHours(n) {
  const num = Number(n) || 0;
  return num.toFixed(num % 1 === 0 ? 0 : 1);
}
function formatMinutesAsHM(mins) {
  const m = Math.round(Number(mins) || 0);
  const h = Math.floor(m / 60), rem = m % 60;
  if (h === 0) return `${rem}m`;
  if (rem === 0) return `${h}h`;
  return `${h}h ${rem}m`;
}
function formatDateTime(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function formatDateOnly(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function csvEscape(val) {
  const s = String(val ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadCsv(filename, rows) {
  const csv = rows.map(r => r.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Small shared UI bits ────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = '#7c3aed' }) {
  return (
    <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: '16px 18px', flex: 1, minWidth: 150 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ProgressBar({ pct, color = '#7c3aed' }) {
  const clamped = pct == null ? null : Math.max(0, Math.min(150, pct));
  if (clamped == null) return <span style={{ fontSize: 11, color: '#94a3b8' }}>No target set</span>;
  const barColor = clamped >= 100 ? '#10b981' : clamped >= 60 ? color : '#f59e0b';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 4, background: '#f1f5f9', overflow: 'hidden', minWidth: 60 }}>
        <div style={{ width: `${Math.min(100, clamped)}%`, height: '100%', background: barColor, borderRadius: 4 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: barColor, minWidth: 36, textAlign: 'right' }}>{Math.round(pct)}%</span>
    </div>
  );
}

// ─── Date navigator — prev/next day + picker, capped at today ───────────────
function DateNav({ date, onChange }) {
  const isToday = date === todayIso();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button className="btn-ghost" onClick={() => onChange(shiftDateIso(date, -1))} style={{ fontSize: 13, padding: '6px 10px' }} title="Previous day">‹</button>
      <input type="date" className="crm-input" value={date} max={todayIso()} onChange={e => e.target.value && onChange(e.target.value)} style={{ width: 150 }} />
      <button className="btn-ghost" onClick={() => onChange(shiftDateIso(date, 1))} disabled={isToday} style={{ fontSize: 13, padding: '6px 10px', opacity: isToday ? 0.4 : 1 }} title="Next day">›</button>
      {!isToday && (
        <button className="btn-ghost" onClick={() => onChange(todayIso())} style={{ fontSize: 11 }}>Today</button>
      )}
    </div>
  );
}

// ─── Daily Report — one employee's stats for one specific day ───────────────
function DailyReportPanel({ meetings, emailActivity, workLogs, userId, date }) {
  const dayMeetings = useMemo(
    () => meetings.filter(m => m.userId === userId && m.start && toISODate(new Date(m.start)) === date)
                  .sort((a, b) => (a.start || 0) - (b.start || 0)),
    [meetings, userId, date]
  );
  const dayLogs = useMemo(
    () => workLogs.filter(l => l.userId === userId && l.date === date),
    [workLogs, userId, date]
  );
  const dayEmail = useMemo(
    () => emailActivity.filter(e => e.userId === userId && e.date === date),
    [emailActivity, userId, date]
  );

  const meetingHours = dayMeetings.reduce((s, m) => s + (m.durationMinutes || 0), 0) / 60;
  const logHours = dayLogs.reduce((s, l) => s + (Number(l.hoursSpent) || 0), 0);
  const sent = dayEmail.reduce((s, e) => s + (e.sent || 0), 0);
  const received = dayEmail.reduce((s, e) => s + (e.received || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
        <StatCard label="Meetings" value={dayMeetings.length} />
        <StatCard label="Meeting hours" value={formatHours(meetingHours)} color="#0078d4" />
        <StatCard label="Logged hours" value={formatHours(logHours)} color="#10b981" />
        <StatCard label="Emails (sent/recv)" value={`${sent} / ${received}`} color="#f59e0b" />
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Meetings that day</div>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, overflow: 'hidden', marginBottom: 18 }}>
        {dayMeetings.length === 0 ? (
          <div style={{ padding: 18, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No synced meetings on this day.</div>
        ) : dayMeetings.map((m, i) => (
          <div key={m.id || i} style={{ padding: '10px 14px', borderBottom: i < dayMeetings.length - 1 ? '1px solid #f1f5f9' : 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16 }}>{m.platform === 'zoom' ? '🔵' : m.platform === 'teams' ? '📨' : m.platform === 'google_meet' ? '📧' : '📅'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{m.title}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{formatDateTime(m.start)} · {formatMinutesAsHM(m.durationMinutes)} · {m.attendeeCount} attendee(s)</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Self-logged that day</div>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, overflow: 'hidden', marginBottom: 18 }}>
        {dayLogs.length === 0 ? (
          <div style={{ padding: 18, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Nothing self-logged on this day.</div>
        ) : dayLogs.map((l, i) => (
          <div key={l.id || i} style={{ padding: '10px 14px', borderBottom: i < dayLogs.length - 1 ? '1px solid #f1f5f9' : 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16 }}>{CATEGORY_MAP[l.category]?.icon || '🗂️'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{l.description}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{formatHours(l.hoursSpent)}h · {CATEGORY_MAP[l.category]?.label || l.category}{l.withWhom ? ` · with ${l.withWhom}` : ''}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Remarks / Feedback thread — admin ↔ employee, scoped to one day ────────
function RemarksThread({ companyId, userId, date, currentUser, viewerRole }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!companyId || !userId || !date) return;
    const unsub = subscribeRemarkMessages(companyId, userId, date, setMessages);
    return unsub;
  }, [companyId, userId, date]);

  // Mark this thread "seen" by the current viewer whenever it's open and
  // whenever new messages arrive while it stays open.
  useEffect(() => {
    if (!companyId || !userId || !date) return;
    markRemarkThreadSeen(companyId, userId, date, viewerRole);
  }, [companyId, userId, date, viewerRole, messages.length]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || !currentUser?.id) return;
    setSending(true);
    try {
      await addRemarkMessage(companyId, userId, date, {
        fromUserId: currentUser.id,
        fromName: currentUser.name || currentUser.email || (viewerRole === 'admin' ? 'Admin' : 'Employee'),
        fromRole: viewerRole,
        text: text.trim(),
      });
      setText('');
    } finally { setSending(false); }
  };

  return (
    <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
        💬 Remarks &amp; Clarifications — {formatDateOnly(date)}
      </div>
      <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {messages.length === 0 ? (
          <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '16px 0' }}>
            {viewerRole === 'admin' ? 'No remarks yet — leave feedback or ask for clarification below.' : 'No remarks from your manager on this day yet.'}
          </div>
        ) : messages.map(m => (
          <div key={m.id} style={{
            alignSelf: m.fromRole === viewerRole ? 'flex-end' : 'flex-start',
            maxWidth: '78%',
            background: m.fromRole === 'admin' ? 'rgba(124,58,237,0.08)' : 'rgba(16,185,129,0.08)',
            border: `1px solid ${m.fromRole === 'admin' ? 'rgba(124,58,237,0.2)' : 'rgba(16,185,129,0.2)'}`,
            borderRadius: 10, padding: '8px 12px',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: m.fromRole === 'admin' ? '#7c3aed' : '#059669', marginBottom: 3 }}>
              {m.fromName || (m.fromRole === 'admin' ? 'Admin' : 'Employee')}
            </div>
            <div style={{ fontSize: 13, color: '#0f172a', whiteSpace: 'pre-wrap' }}>{m.text}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>
              {m.createdAt?.toDate ? formatDateTime(m.createdAt.toDate().getTime()) : 'sending…'}
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={handleSend} style={{ display: 'flex', gap: 8 }}>
        <input
          className="crm-input" style={{ flex: 1 }}
          placeholder={viewerRole === 'admin' ? 'Leave a remark, feedback, or ask for clarification…' : 'Reply to your manager…'}
          value={text} onChange={e => setText(e.target.value)}
        />
        <button type="submit" className="btn-primary" disabled={sending || !text.trim()} style={{ fontSize: 12 }}>
          {sending ? '…' : 'Send'}
        </button>
      </form>
    </div>
  );
}

// ─── My Activity tab ──────────────────────────────────────────────────────────
function MyActivityTab({ currentUser, companyId, banner, onDismissBanner }) {
  const [myMeetings, setMyMeetings] = useState([]);
  const [myEmail, setMyEmail] = useState([]);
  const [myLogs, setMyLogs] = useState([]);
  const [logForm, setLogForm] = useState({ date: todayIso(), category: 'meeting', withWhom: '', hoursSpent: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [myDailyDate, setMyDailyDate] = useState(todayIso());
  const [myThreadsMeta, setMyThreadsMeta] = useState([]);

  useEffect(() => {
    if (!companyId || !currentUser?.id) return;
    const u1 = subscribeUserSyncedMeetings(companyId, currentUser.id, setMyMeetings);
    const u2 = subscribeEmailActivity(companyId, all => setMyEmail(all.filter(e => e.userId === currentUser.id)));
    const u3 = subscribeUserWorkLogs(companyId, currentUser.id, setMyLogs);
    const u4 = subscribeAllRemarkThreadsMeta(companyId, all => setMyThreadsMeta(all.filter(t => t.userId === currentUser.id)));
    return () => { u1(); u2(); u3(); u4(); };
  }, [companyId, currentUser?.id]);

  // Most recent day where the manager left a remark this employee hasn't seen yet.
  const unseenRemarkThread = useMemo(() => {
    const unseen = myThreadsMeta.filter(t => t.employeeUnread && t.date);
    if (!unseen.length) return null;
    return unseen.sort((a, b) => (b.date || '').localeCompare(a.date || ''))[0];
  }, [myThreadsMeta]);

  const handleAddLog = async (e) => {
    e.preventDefault();
    if (!logForm.description.trim() || !logForm.hoursSpent) return;
    setSaving(true);
    try {
      await addWorkLog(companyId, currentUser.id, {
        date: logForm.date,
        category: logForm.category,
        withWhom: logForm.withWhom.trim() || null,
        hoursSpent: Number(logForm.hoursSpent) || 0,
        description: logForm.description.trim(),
        userName: currentUser.name,
      });
      setLogForm({ date: todayIso(), category: 'meeting', withWhom: '', hoursSpent: '', description: '' });
    } finally { setSaving(false); }
  };

  // Merge meetings + work logs into one reverse-chronological feed.
  const feed = useMemo(() => {
    const items = [
      ...myMeetings.map(m => ({ kind: 'synced', ts: m.start || 0, ...m })),
      ...myLogs.map(l => ({ kind: 'logged', ts: new Date(l.date).getTime() || 0, ...l })),
    ];
    items.sort((a, b) => b.ts - a.ts);
    return items.slice(0, 50);
  }, [myMeetings, myLogs]);

  const last30MeetingHours = useMemo(() => {
    const since = daysAgoMs(30);
    return myMeetings.filter(m => m.start >= since).reduce((s, m) => s + (m.durationMinutes || 0), 0) / 60;
  }, [myMeetings]);
  const last30LogHours = useMemo(() => {
    const since = daysAgoIso(30);
    return myLogs.filter(l => l.date >= since).reduce((s, l) => s + (Number(l.hoursSpent) || 0), 0);
  }, [myLogs]);
  const last30Email = useMemo(() => {
    const since = daysAgoIso(30);
    return myEmail.filter(e => e.date >= since).reduce((acc, e) => ({ sent: acc.sent + (e.sent || 0), received: acc.received + (e.received || 0) }), { sent: 0, received: 0 });
  }, [myEmail]);

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      {banner && (
        <div style={{
          marginBottom: 18, padding: '12px 16px', borderRadius: 10,
          background: banner.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
          border: `1px solid ${banner.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'}`,
          color: banner.type === 'error' ? '#991b1b' : '#065f46',
          fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>{banner.message}</span>
          <button onClick={onDismissBanner} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 16, color: 'inherit' }}>×</button>
        </div>
      )}

      {unseenRemarkThread && (
        <div style={{
          marginBottom: 18, padding: '12px 16px', borderRadius: 10,
          background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.25)',
          fontSize: 13, color: '#5b21b6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
        }}>
          <span>💬 Your manager left a new remark on <strong>{formatDateOnly(unseenRemarkThread.date)}</strong>{unseenRemarkThread.lastMessageText ? `: "${unseenRemarkThread.lastMessageText.slice(0, 80)}${unseenRemarkThread.lastMessageText.length > 80 ? '…' : ''}"` : ''}</span>
          <button className="btn-primary" style={{ fontSize: 11 }} onClick={() => setMyDailyDate(unseenRemarkThread.date)}>View &amp; reply</button>
        </div>
      )}

      {/* Daily Report & Manager Remarks */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>Daily Report</h3>
        <DateNav date={myDailyDate} onChange={setMyDailyDate} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 18, marginBottom: 28, alignItems: 'start' }}>
        <DailyReportPanel meetings={myMeetings} emailActivity={myEmail} workLogs={myLogs} userId={currentUser.id} date={myDailyDate} />
        <RemarksThread companyId={companyId} userId={currentUser.id} date={myDailyDate} currentUser={currentUser} viewerRole="employee" />
      </div>

      {/* Quick stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 22, flexWrap: 'wrap' }}>
        <StatCard label="Meeting hours (30d)" value={formatHours(last30MeetingHours)} sub="auto-synced" />
        <StatCard label="Self-logged hours (30d)" value={formatHours(last30LogHours)} sub="manually logged" color="#0078d4" />
        <StatCard label="Emails sent (30d)" value={last30Email.sent} color="#10b981" />
        <StatCard label="Emails received (30d)" value={last30Email.received} color="#f59e0b" />
      </div>

      {/* Self-log form */}
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Log Today's Work</h3>
      <p style={{ fontSize: 12, color: '#64748b', marginTop: 0, marginBottom: 14 }}>
        Use this for anything that wouldn't show up automatically — an in-person meeting, a phone call, follow-up outreach, or admin work. Not limited to CRM students.
      </p>
      <form onSubmit={handleAddLog} style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: 18, marginBottom: 28 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Date</label>
            <input type="date" className="crm-input" value={logForm.date} max={todayIso()} onChange={e => setLogForm(p => ({ ...p, date: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Category</label>
            <select className="crm-input" value={logForm.category} onChange={e => setLogForm(p => ({ ...p, category: e.target.value }))}>
              {LOG_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>With whom (optional)</label>
            <input className="crm-input" placeholder="e.g. Walk-in student, vendor…" value={logForm.withWhom} onChange={e => setLogForm(p => ({ ...p, withWhom: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Hours spent</label>
            <input type="number" min="0" step="0.25" className="crm-input" placeholder="1.5" value={logForm.hoursSpent} onChange={e => setLogForm(p => ({ ...p, hoursSpent: e.target.value }))} />
          </div>
        </div>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>What did you work on?</label>
        <textarea className="crm-input" rows={2} placeholder="Briefly describe the activity…" value={logForm.description} onChange={e => setLogForm(p => ({ ...p, description: e.target.value }))} style={{ width: '100%', resize: 'vertical', marginBottom: 12 }} />
        <button type="submit" className="btn-primary" disabled={saving} style={{ fontSize: 12 }}>
          {saving ? 'Saving…' : '+ Add Log Entry'}
        </button>
      </form>

      {/* Recent activity feed */}
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Recent Activity</h3>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, overflow: 'hidden' }}>
        {feed.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No activity yet — connect an account or log something above.</div>
        ) : feed.map((item, i) => (
          <div key={item.id || i} style={{ padding: '12px 16px', borderBottom: i < feed.length - 1 ? '1px solid #f1f5f9' : 'none', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 18 }}>{item.kind === 'synced' ? (item.platform === 'zoom' ? '🔵' : item.platform === 'teams' ? '📨' : item.platform === 'google_meet' ? '📧' : '📅') : (CATEGORY_MAP[item.category]?.icon || '🗂️')}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{item.kind === 'synced' ? item.title : item.description}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                {item.kind === 'synced' ? `${formatDateTime(item.start)} · ${formatMinutesAsHM(item.durationMinutes)} · ${item.attendeeCount} attendee(s)` : `${formatDateOnly(item.date)} · ${formatHours(item.hoursSpent)}h · ${CATEGORY_MAP[item.category]?.label || item.category}${item.withWhom ? ` · with ${item.withWhom}` : ''}`}
              </div>
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, color: item.kind === 'synced' ? '#7c3aed' : '#0078d4', background: item.kind === 'synced' ? 'rgba(124,58,237,0.08)' : 'rgba(0,120,212,0.08)', padding: '3px 8px', borderRadius: 20 }}>
              {item.kind === 'synced' ? 'Synced' : 'Self-logged'}
            </span>
            {item.kind === 'logged' && (
              <button onClick={() => deleteWorkLog(companyId, item.id)} title="Delete" style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: 14 }}>✕</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Team Report tab (admin/superadmin only) ─────────────────────────────────
function TeamReportTab({ companyId, users, currentUser }) {
  const [mode, setMode] = useState('range'); // 'range' | 'daily'
  const [range, setRange] = useState(30);
  const [meetings, setMeetings] = useState([]);
  const [emailActivity, setEmailActivity] = useState([]);
  const [workLogs, setWorkLogs] = useState([]);
  const [targets, setTargets] = useState({});
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [targetEdit, setTargetEdit] = useState(null); // { userId, weeklyMeetingsTarget, weeklyHoursTarget }
  const [remarkThreads, setRemarkThreads] = useState([]);
  const [dailyEmployeeId, setDailyEmployeeId] = useState(users[0]?.id || '');
  const [dailyDate, setDailyDate] = useState(todayIso());

  useEffect(() => {
    if (!companyId) return;
    const u1 = subscribeSyncedMeetings(companyId, setMeetings);
    const u2 = subscribeEmailActivity(companyId, setEmailActivity);
    const u3 = subscribeWorkLogs(companyId, setWorkLogs);
    const u4 = subscribeWorkTargets(companyId, setTargets);
    const u5 = subscribeAllRemarkThreadsMeta(companyId, setRemarkThreads);
    return () => { u1(); u2(); u3(); u4(); u5(); };
  }, [companyId]);

  // Pick a default employee for the Daily Reports view once the user list loads.
  useEffect(() => {
    if (!dailyEmployeeId && users.length) setDailyEmployeeId(users[0].id);
  }, [users, dailyEmployeeId]);

  // Employees who have replied to a remark the admin hasn't seen yet.
  const unreadReplyUserIds = useMemo(
    () => new Set(remarkThreads.filter(t => t.adminUnread).map(t => t.userId)),
    [remarkThreads]
  );
  const totalUnreadReplies = unreadReplyUserIds.size;

  const sinceMs = daysAgoMs(range);
  const sinceIso = daysAgoIso(range);
  const weeks = range / 7;

  const rangeMeetings = useMemo(() => meetings.filter(m => (m.start || 0) >= sinceMs), [meetings, sinceMs]);
  const rangeEmail = useMemo(() => emailActivity.filter(e => (e.date || '') >= sinceIso), [emailActivity, sinceIso]);
  const rangeLogs = useMemo(() => workLogs.filter(l => (l.date || '') >= sinceIso), [workLogs, sinceIso]);

  const perUser = useMemo(() => {
    return users.map(u => {
      const myMeetings = rangeMeetings.filter(m => m.userId === u.id);
      const myEmail = rangeEmail.filter(e => e.userId === u.id);
      const myLogs = rangeLogs.filter(l => l.userId === u.id);
      const meetingHours = myMeetings.reduce((s, m) => s + (m.durationMinutes || 0), 0) / 60;
      const logHours = myLogs.reduce((s, l) => s + (Number(l.hoursSpent) || 0), 0);
      const totalHours = meetingHours + logHours;
      const sent = myEmail.reduce((s, e) => s + (e.sent || 0), 0);
      const received = myEmail.reduce((s, e) => s + (e.received || 0), 0);
      const uniqueLogDays = new Set(myLogs.map(l => l.date)).size;
      const adherencePct = Math.min(100, (uniqueLogDays / Math.max(1, range)) * 100);
      const target = targets[u.id] || targets.default || null;
      const targetMeetings = target ? (Number(target.weeklyMeetingsTarget) || 0) * weeks : 0;
      const targetHours = target ? (Number(target.weeklyHoursTarget) || 0) * weeks : 0;
      const meetingPct = targetMeetings > 0 ? (myMeetings.length / targetMeetings) * 100 : null;
      const hoursPct = targetHours > 0 ? (totalHours / targetHours) * 100 : null;
      return {
        user: u, meetingCount: myMeetings.length, meetingHours, logHours, totalHours,
        sent, received, adherencePct, meetingPct, hoursPct,
        targetMeetings, targetHours,
      };
    });
  }, [users, rangeMeetings, rangeEmail, rangeLogs, targets, range, weeks]);

  const visibleRows = employeeFilter === 'all' ? perUser : perUser.filter(r => r.user.id === employeeFilter);

  const leaderboard = useMemo(() => [...perUser].sort((a, b) => b.totalHours - a.totalHours).slice(0, 5), [perUser]);

  const chartData = useMemo(() => perUser.map(r => ({
    name: (r.user.name || r.user.email || '?').split(' ')[0],
    'Meeting hrs': Math.round(r.meetingHours * 10) / 10,
    'Logged hrs': Math.round(r.logHours * 10) / 10,
  })), [perUser]);

  const handleExportCsv = () => {
    const header = ['Employee', 'Email', 'Meetings', 'Meeting Hours', 'Self-Logged Hours', 'Total Hours', 'Emails Sent', 'Emails Received', 'Log Adherence %'];
    const rows = perUser.map(r => [
      r.user.name || '', r.user.email || '', r.meetingCount, formatHours(r.meetingHours),
      formatHours(r.logHours), formatHours(r.totalHours), r.sent, r.received,
      Math.round(r.adherencePct),
    ]);
    downloadCsv(`work-report_${todayIso()}.csv`, [header, ...rows]);
  };

  const handleSaveTarget = async () => {
    if (!targetEdit) return;
    await saveWorkTarget(companyId, targetEdit.userId, {
      weeklyMeetingsTarget: Number(targetEdit.weeklyMeetingsTarget) || 0,
      weeklyHoursTarget: Number(targetEdit.weeklyHoursTarget) || 0,
    });
    setTargetEdit(null);
  };

  const totalMeetingHours = perUser.reduce((s, r) => s + r.meetingHours, 0);
  const totalLogHours = perUser.reduce((s, r) => s + r.logHours, 0);
  const totalMeetings = perUser.reduce((s, r) => s + r.meetingCount, 0);
  const totalEmails = perUser.reduce((s, r) => s + r.sent + r.received, 0);

  return (
    <div style={{ padding: 24, maxWidth: 1300, margin: '0 auto' }}>
      {/* Mode toggle */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        <button onClick={() => setMode('range')} style={{
          padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
          border: mode === 'range' ? '1px solid var(--color-primary, #7c3aed)' : '1px solid #e2e8f0',
          background: mode === 'range' ? 'rgba(124,58,237,0.08)' : '#fff',
          color: mode === 'range' ? 'var(--color-primary, #7c3aed)' : '#64748b',
        }}>📊 Range Summary</button>
        <button onClick={() => setMode('daily')} style={{
          padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', position: 'relative',
          border: mode === 'daily' ? '1px solid var(--color-primary, #7c3aed)' : '1px solid #e2e8f0',
          background: mode === 'daily' ? 'rgba(124,58,237,0.08)' : '#fff',
          color: mode === 'daily' ? 'var(--color-primary, #7c3aed)' : '#64748b',
        }}>
          📅 Daily Reports
          {totalUnreadReplies > 0 && (
            <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#fff', background: '#ef4444', borderRadius: 20, padding: '1px 6px' }}>
              {totalUnreadReplies}
            </span>
          )}
        </button>
      </div>

      {mode === 'daily' && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <select className="crm-input" style={{ width: 240 }} value={dailyEmployeeId} onChange={e => setDailyEmployeeId(e.target.value)}>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {(u.name || u.email)}{unreadReplyUserIds.has(u.id) ? ' 🔴 new reply' : ''}
                </option>
              ))}
            </select>
            <DateNav date={dailyDate} onChange={setDailyDate} />
          </div>
          {users.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No employees yet.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 18, alignItems: 'start' }}>
              <DailyReportPanel meetings={meetings} emailActivity={emailActivity} workLogs={workLogs} userId={dailyEmployeeId} date={dailyDate} />
              <RemarksThread companyId={companyId} userId={dailyEmployeeId} date={dailyDate} currentUser={currentUser} viewerRole="admin" />
            </div>
          )}
        </div>
      )}

      {mode === 'range' && (<>
      {/* Range selector */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {RANGE_OPTIONS.map(r => (
            <button key={r.id} onClick={() => setRange(r.id)} style={{
              padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: range === r.id ? '1px solid var(--color-primary, #7c3aed)' : '1px solid #e2e8f0',
              background: range === r.id ? 'rgba(124,58,237,0.08)' : '#fff',
              color: range === r.id ? 'var(--color-primary, #7c3aed)' : '#64748b',
            }}>{r.label}</button>
          ))}
        </div>
        <button className="btn-ghost" onClick={handleExportCsv} style={{ fontSize: 12 }}>⬇ Export CSV</button>
      </div>

      {/* Top-line stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 22, flexWrap: 'wrap' }}>
        <StatCard label="Total meetings" value={totalMeetings} sub={`across ${users.length} employees`} />
        <StatCard label="Meeting hours" value={formatHours(totalMeetingHours)} color="#0078d4" />
        <StatCard label="Self-logged hours" value={formatHours(totalLogHours)} color="#10b981" />
        <StatCard label="Total emails" value={totalEmails} color="#f59e0b" />
      </div>


      {/* Chart */}
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: 18, marginBottom: 22, height: 280 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Tracked Hours per Employee</div>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 0, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Meeting hrs" stackId="a" fill="#0078d4" radius={[0, 0, 0, 0]} />
            <Bar dataKey="Logged hrs" stackId="a" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Leaderboard */}
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: 18, marginBottom: 22 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>🏆 Top Tracked Hours</div>
        {leaderboard.map((r, i) => (
          <div key={r.user.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
            <span style={{ fontSize: 13 }}>{['🥇', '🥈', '🥉', '4.', '5.'][i]}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', flex: 1 }}>{r.user.name || r.user.email}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>{formatHours(r.totalHours)}h</span>
          </div>
        ))}
      </div>

      {/* Per-employee table */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>Per-Employee Summary</h3>
        <select className="crm-input" style={{ width: 200 }} value={employeeFilter} onChange={e => setEmployeeFilter(e.target.value)}>
          <option value="all">All employees</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
      </div>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, overflow: 'hidden', marginBottom: 28 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
              {['Employee', 'Meetings', 'Meeting Hrs', 'Logged Hrs', 'Total Hrs', 'Emails (sent/recv)', 'Log Adherence', 'Meetings vs Target', 'Hours vs Target', ''].map(h => (
                <th key={h} style={{ padding: '10px 12px', fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(r => (
              <tr key={r.user.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0f172a' }}>{r.user.name || r.user.email}</td>
                <td style={{ padding: '10px 12px' }}>{r.meetingCount}</td>
                <td style={{ padding: '10px 12px' }}>{formatHours(r.meetingHours)}h</td>
                <td style={{ padding: '10px 12px' }}>{formatHours(r.logHours)}h</td>
                <td style={{ padding: '10px 12px', fontWeight: 700 }}>{formatHours(r.totalHours)}h</td>
                <td style={{ padding: '10px 12px' }}>{r.sent} / {r.received}</td>
                <td style={{ padding: '10px 12px' }}>{Math.round(r.adherencePct)}%</td>
                <td style={{ padding: '10px 12px', minWidth: 110 }}><ProgressBar pct={r.meetingPct} /></td>
                <td style={{ padding: '10px 12px', minWidth: 110 }}><ProgressBar pct={r.hoursPct} color="#0078d4" /></td>
                <td style={{ padding: '10px 12px' }}>
                  <button onClick={() => setTargetEdit({ userId: r.user.id, weeklyMeetingsTarget: targets[r.user.id]?.weeklyMeetingsTarget || '', weeklyHoursTarget: targets[r.user.id]?.weeklyHoursTarget || '' })} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>
                    Set target
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Meeting-by-meeting detail log */}
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Meeting Detail Log</h3>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, overflow: 'hidden', maxHeight: 420, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc', textAlign: 'left', position: 'sticky', top: 0 }}>
              {['Employee', 'Title', 'Platform', 'When', 'Duration', 'Attendees'].map(h => (
                <th key={h} style={{ padding: '10px 12px', fontWeight: 700, color: '#64748b' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rangeMeetings.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>No synced meetings in this range yet.</td></tr>
            ) : [...rangeMeetings]
              .filter(m => employeeFilter === 'all' || m.userId === employeeFilter)
              .sort((a, b) => (b.start || 0) - (a.start || 0))
              .map(m => {
                const owner = users.find(u => u.id === m.userId);
                return (
                  <tr key={m.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 12px' }}>{owner?.name || owner?.email || m.userId}</td>
                    <td style={{ padding: '8px 12px' }}>{m.title}</td>
                    <td style={{ padding: '8px 12px', textTransform: 'capitalize' }}>{(m.platform || '').replace('_', ' ')}</td>
                    <td style={{ padding: '8px 12px' }}>{formatDateTime(m.start)}</td>
                    <td style={{ padding: '8px 12px' }}>{formatMinutesAsHM(m.durationMinutes)}</td>
                    <td style={{ padding: '8px 12px' }}>{m.attendeeCount}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      </>)}

      {/* Target editor modal */}
      {targetEdit && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={() => setTargetEdit(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 22, width: 320 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Set Weekly Target</div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Target meetings / week</label>
            <input type="number" min="0" className="crm-input" value={targetEdit.weeklyMeetingsTarget} onChange={e => setTargetEdit(p => ({ ...p, weeklyMeetingsTarget: e.target.value }))} style={{ marginBottom: 12, width: '100%' }} />
            <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Target hours / week</label>
            <input type="number" min="0" className="crm-input" value={targetEdit.weeklyHoursTarget} onChange={e => setTargetEdit(p => ({ ...p, weeklyHoursTarget: e.target.value }))} style={{ marginBottom: 16, width: '100%' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={() => setTargetEdit(null)} style={{ fontSize: 12 }}>Cancel</button>
              <button className="btn-primary" onClick={handleSaveTarget} style={{ fontSize: 12 }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function WorkReport() {
  const { currentUser, company, companyId, users } = useCRM();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const [view, setView] = useState(isAdmin ? 'team' : 'my');
  const [banner, setBanner] = useState(null);

  // Pick up ?connected=provider / ?oauthError=message from the OAuth
  // callback redirect, show a banner once, then strip them from the URL so a
  // refresh doesn't re-show the same message.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const oauthError = params.get('oauthError');
    if (connected) {
      setBanner({ type: 'success', message: `✅ ${connected.charAt(0).toUpperCase() + connected.slice(1)} account connected successfully.` });
    } else if (oauthError) {
      setBanner({ type: 'error', message: `Connection failed: ${oauthError}` });
    }
    if (connected || oauthError) {
      params.delete('connected'); params.delete('oauthError');
      const rest = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
  }, []);

  const cid = companyId || company?.id;

  return (
    <div style={{ height: '100%', overflowY: 'auto', boxSizing: 'border-box', background: 'var(--bg-secondary, #f8fafc)' }}>
      <div style={{
        background: '#fff', borderBottom: '1px solid #e2e8f0',
        padding: '0 28px', display: 'flex', alignItems: 'center', gap: 0,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button onClick={() => setView('my')} style={{
          padding: '14px 20px', border: 'none', background: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 600,
          color: view === 'my' ? 'var(--color-primary, #7c3aed)' : '#64748b',
          borderBottom: view === 'my' ? '2px solid var(--color-primary, #7c3aed)' : '2px solid transparent',
        }}>
          My Activity
        </button>
        {isAdmin && (
          <button onClick={() => setView('team')} style={{
            padding: '14px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 600,
            color: view === 'team' ? 'var(--color-primary, #7c3aed)' : '#64748b',
            borderBottom: view === 'team' ? '2px solid var(--color-primary, #7c3aed)' : '2px solid transparent',
          }}>
            Team Report
          </button>
        )}
      </div>

      {view === 'my' && (
        <MyActivityTab currentUser={currentUser} companyId={cid} banner={banner} onDismissBanner={() => setBanner(null)} />
      )}
      {view === 'team' && isAdmin && (
        <TeamReportTab companyId={cid} users={users} currentUser={currentUser} />
      )}
    </div>
  );
}
